/**
 * Wertzelle der neuen Periodentabelle (D-13/D-14/D-17, Sketch 023 Variante C): traegt alle
 * pruefbaren Zustaende als `data-*`-Attribute und den vollstaendigen Erklaerungstext im `title`.
 * Rendert selbst keine Rechenlogik -- Wert, Zustand und Rueckrechenbarkeit kommen fertig aus dem
 * `CellInfo` des Servers (39-08), hier wird nur formatiert und dargestellt.
 *
 * Klick oeffnet das Klick-Menue (CellLockMenu, Task 3) ueber `onOpen` -- auch im importierten
 * Zeitraum, das Menue selbst zeigt dort nur Werte ohne Aktionen (`editable=false`, Task 3).
 */
import {
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Table } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { cellColorVars } from './evaluationColorVars';
import { formatPercent, formatShare, formatTonnes } from './evaluationFormat';
import type { CellInfo } from '../../types/evaluationTable';
import classes from './evaluationTable.module.css';

export type EvaluationCellUnit = 'tonnes' | 'percent';

function cx(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function displayValue(value: string | null, unit: EvaluationCellUnit): string {
  return unit === 'percent' ? formatShare(value) : formatTonnes(value);
}

function isZeroValue(value: string | null): boolean {
  return value !== null && Number(value) === 0;
}

function buildTitle(cell: CellInfo, unit: EvaluationCellUnit): string {
  const unitSuffix = unit === 'percent' ? '%' : 't';
  const valuePart =
    unit === 'percent' ? formatPercent(cell.value) : formatTonnes(cell.value);
  let title = `${cell.label}: ${valuePart} ${unitSuffix}`;

  if (cell.state === 'locked_rule' && cell.compliance) {
    title += ` – Gesperrt nach Regel: ${cell.compliance.problems.join('; ')}`;
  } else if (cell.state === 'locked_manual') {
    title += ' – Von Hand gesperrt';
  } else if (cell.state === 'released_manual') {
    title += ' – Trotz Regel freigegeben';
  }

  if (cell.recoverable) {
    title += ` – lässt sich zurückrechnen: ${cell.recoverable.description}`;
  }

  return title;
}

export interface EvaluationValueCellProps {
  cell: CellInfo;
  unit: EvaluationCellUnit;
  highlighted?: boolean;
  onOpen: (cellKey: string, anchor: HTMLElement) => void;
  menu?: ReactNode;
  /** Zementarten-Klassenzellen: eine echte Null wird grau statt schwarz dargestellt. */
  zeroMuted?: boolean;
  bold?: boolean;
  /** Zusaetzliche Zellstile, etwa die Gruppengrenze (`GROUP_DIVIDER`) am Spaltengruppenanfang. */
  style?: CSSProperties;
}

export function EvaluationValueCell({
  cell,
  unit,
  highlighted = false,
  onOpen,
  menu,
  zeroMuted = false,
  bold = false,
  style,
}: EvaluationValueCellProps) {
  const ref = useRef<HTMLTableCellElement>(null);

  const isManual =
    cell.state === 'locked_manual' || cell.state === 'released_manual';
  const isForced = cell.state === 'released_manual';
  const zero = zeroMuted && isZeroValue(cell.value);

  const handleOpen = () => {
    if (ref.current) {
      onOpen(cell.cellKey, ref.current);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <Table.Td
      ref={ref}
      data-cell-key={cell.cellKey}
      data-state={cell.state}
      data-blocked={cell.blocked ? 'true' : 'false'}
      data-recoverable={cell.recoverable ? 'true' : 'false'}
      tabIndex={0}
      role="button"
      aria-haspopup="dialog"
      title={buildTitle(cell, unit)}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      style={{
        ...cellColorVars,
        ...(bold ? { fontWeight: 700 } : {}),
        ...style,
      }}
      className={cx(
        classes.num,
        classes.cell,
        cell.blocked && classes.blocked,
        isManual && classes.manual,
        isForced && classes.forced,
        Boolean(cell.recoverable) && classes.recoverable,
        highlighted && classes.highlight,
        zero && classes.zero
      )}
    >
      {zero ? '0' : displayValue(cell.value, unit)}
      {menu}
    </Table.Td>
  );
}

export interface PreviousValueCellProps {
  value: string | null;
  previousLocked: boolean;
  unit: EvaluationCellUnit;
  style?: CSSProperties;
}

const PREV_LOCKED_TITLE =
  'Im Vorjahr gesperrt – wird nicht übertragen und ist hier nicht änderbar';

/** Vorjahreszelle -- nie klickbar, D-16: Sperren des Vorjahres sind endgueltig. */
export function PreviousValueCell({
  value,
  previousLocked,
  unit,
  style,
}: PreviousValueCellProps) {
  return (
    <Table.Td
      data-prev-locked={previousLocked ? 'true' : 'false'}
      className={cx(classes.num, previousLocked && classes.prevLocked)}
      title={previousLocked ? PREV_LOCKED_TITLE : undefined}
      style={style}
    >
      {previousLocked && <IconLock size={12} />} {displayValue(value, unit)}
    </Table.Td>
  );
}
